/**
 * Created by katarinababic on 23.6.25.
 */
import * as React from 'react';
import { observer } from 'mobx-react';
import { useState } from 'react';
import { WeaviateSuggestionResult, WeaviateSuggestionResultApi } from '../../Model/Suggestions/WeaviateSuggestionResult';
import { useRootStore } from '../../Store/useRootStore';
import { useLocalized } from '../../Hooks/useLocalized';
import { useLocalizedImage } from '../../Hooks/useLocalizedMedia';
import { useTrans } from '../../Store/System/LocalizationStore';
import { FitnessProgramTemplate } from '../../Model/Coach/Program/Template/FitnessProgramTemplate';
import { StringUtils } from '../../Utils/StringUtils';
import { useNavigate } from 'react-router-dom';
import { HomeScreenSuggestionItem } from './HomeScreenSuggestionItem';
import { AlertModal } from '../../Components/AlertModal';
import { useSecondaryText } from '../../Utils/ExploreUtils';
import { ExploreEntry } from '../../Model/Explore/ExploreEntry';
import { getMediaLargeOrMediumOrSmallest } from '../../Model/Media/Media';

const isFitnessProgramTemplate = (entry: FitnessProgramTemplate | ExploreEntry | undefined): entry is FitnessProgramTemplate =>
  !!entry && 'templateName' in entry && 'mesoCycles' in entry;

export type HomeScreenFitnessSuggestionItemProps = {
  suggestion: WeaviateSuggestionResult;
  onRefresh?: () => void;
};

export const HomeScreenFitnessSuggestionItem: React.FC<HomeScreenFitnessSuggestionItemProps> = observer(
  ({ suggestion, onRefresh }) => {
    const { authentication, analytics } = useRootStore();
    const navigate = useNavigate();
    const { t } = useTrans();
    const entryName = useLocalized(suggestion.fitnessProgramTemplateOrExploreEntry?.name);
    const athlete = authentication.athlete;

    const [loading, setLoading] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);

    const entry = suggestion.fitnessProgramTemplateOrExploreEntry;
    // For ExploreEntry, use localized image; for FitnessProgramTemplate, use tag-based getImage
    const exploreEntryImage = useLocalizedImage(
      !isFitnessProgramTemplate(entry) ? entry?.images : undefined
    );
    const fitnessImage = isFitnessProgramTemplate(entry) ? entry?.getImage(athlete?.tags ?? []) : undefined;
    const imageUrl = fitnessImage
      ? getMediaLargeOrMediumOrSmallest(fitnessImage)
      : exploreEntryImage
        ? getMediaLargeOrMediumOrSmallest(exploreEntryImage)
        : undefined;

    const imageSrc = imageUrl || require('../../Assets/Images/logo.png');
    const title = entryName ? StringUtils.shortenString(entryName, 40) : '';
    const typeTag = isFitnessProgramTemplate(entry)
      ? t('coach_tab.suggestions.typeTag.fitnessProgram', { defaultValue: 'Fitness Program' })
      : t('coach_tab.suggestions.typeTag.workout', { defaultValue: 'Workout' });

    const handlePress = React.useCallback(() => {
      if (!entry) return;
      analytics.logEvent('coachTabFitnessSuggestion', { entryId: entry.id });
      if (isFitnessProgramTemplate(entry)) {
        // TODO implement navigation after the programs are implemented, show an alert for now
        setModalOpen(true);
      } else {
        navigate(`/move/${entry.id}`);
      }
    }, [analytics, entry, navigate]);

    const handleRefreshSuggestion = React.useCallback(() => {
      if (athlete) {
        setLoading(true);
        analytics.logEvent('refresh_suggestion', {
          category: 'fitness',
          ...suggestion.tags.reduce((acc, tag) => ({ ...acc, [tag]: 1 }), {}),
          contentId: suggestion.contentId,
          contentType: suggestion.contentType,
        });
        WeaviateSuggestionResultApi
          .refresh(suggestion.category)
          .then(() => {
            onRefresh?.();
          })
          .catch((error) => {
            console.error('Failed to refresh fitness suggestion:', error);
          })
          .finally(() => setLoading(false));
      }
    }, [suggestion, athlete, analytics, onRefresh]);

    return (
      <React.Fragment>
        <HomeScreenSuggestionItem
          image={imageSrc as string}
          onPress={handlePress}
          title={title}
          secondaryText={useSecondaryText(entry)}
          loading={loading}
          onRefresh={handleRefreshSuggestion}
          refreshDisabled={loading}
          locked={false}
          onboardingSection="physicalHealth"
          suggestionType="fitness"
          typeTag={typeTag}
        />
        <AlertModal
          isOpen={modalOpen}
          title={t('home.dailySuggestions.contentNotAvailable.title', { defaultValue: 'Content Not Available' })}
          body={t('home.dailySuggestions.contentNotAvailable.body', {
            defaultValue:
              "This content is not yet available. We're working on implementing these programs in our web app and they'll be available soon.",
          })}
          cancelText="Close"
          confirmText="OK"
          onCancel={() => setModalOpen(false)}
          onConfirm={() => setModalOpen(false)}
        />
      </React.Fragment>
    );
  },
);
